// src/components/LoadingPage.js
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as THREE from "three";
import "./LoadingPage.css";
import { getPaidCandidates } from "../apiService";

class CustomCurve extends THREE.Curve {
  getPoint(percent) {
    const pi2 = Math.PI * 2;
    const length = 30;
    const radius = 5.6;
    let t = (percent % 0.25) / 0.25;
    t = (percent % 0.25) - (2 * (1 - t) * t * -0.0185 + t * t * 0.25);
    if (Math.floor(percent / 0.25) === 0 || Math.floor(percent / 0.25) === 2) {
      t *= -1;
    }
    const x = length * Math.sin(pi2 * percent);
    const y = radius * Math.cos(pi2 * 3 * percent);
    const z = radius * Math.sin(pi2 * 2 * (percent - t));
    return new THREE.Vector3(x, y, z);
  }
}

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchCandidatesWithRetry = async (womanId, needed, attempt = 1, maxAttempts = 3) => {
  try {
    return await getPaidCandidates(womanId, needed);
  } catch (error) {
    if (error.response && error.response.status === 429 && attempt < maxAttempts) {
      console.warn(`Rate limit hit (attempt ${attempt}). Retrying in 2000ms...`);
      await delay(2000);
      return fetchCandidatesWithRetry(womanId, needed, attempt + 1, maxAttempts);
    } else {
      throw error;
    }
  }
};

const LoadingPage = () => {
  const containerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem("menToShow")) {
      navigate("/welcome");
      return;
    }
    const cleanupInitSwirl = initSwirl();

    const womanId = localStorage.getItem("womanId");
    if (!womanId) {
      navigate("/");
      return cleanupInitSwirl();
    }

    const needed = 10; // Fetch 10 candidates at a time; no limit enforced
    fetchCandidatesWithRetry(womanId, needed)
      .then((data) => {
        if (!data.objects || data.objects.length === 0) {
          navigate("/done");
        } else {
          localStorage.setItem("menToShow", JSON.stringify(data.objects));
          localStorage.setItem("currentIndex", "0"); // Reset index on new fetch
          navigate("/welcome");
        }
      })
      .catch((err) => {
        console.error("Error fetching candidates after retries:", err);
        navigate("/done");
      });

    return cleanupInitSwirl;
  }, [navigate]);

  const initSwirl = () => {
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;
    const camera = new THREE.PerspectiveCamera(65, width / height, 1, 10000);
    camera.position.z = 120;
    const scene = new THREE.Scene();
    const group = new THREE.Group();
    scene.add(group);
    const curve = new CustomCurve();
    const tubeGeometry = new THREE.TubeGeometry(curve, 200, 1.1, 2, true);
    const mesh = new THREE.Mesh(
      tubeGeometry,
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true })
    );
    group.add(mesh);
    const ringcover = new THREE.Mesh(
      new THREE.PlaneGeometry(50, 15, 1),
      new THREE.MeshBasicMaterial({ color: 0x000000, opacity: 0, transparent: true })
    );
    ringcover.position.x = 31;
    ringcover.rotation.y = Math.PI / 2;
    group.add(ringcover);
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(4.3, 5.55, 32),
      new THREE.MeshBasicMaterial({ color: 0xffffff, opacity: 0, transparent: true })
    );
    ring.position.x = 31.1;
    ring.rotation.y = Math.PI / 2;
    group.add(ring);
    for (let i = 0; i < 10; i++) {
      const plain = new THREE.Mesh(
        new THREE.PlaneGeometry(61, 5.6 * 3, 1),
        new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.13 })
      );
      plain.position.z = -2.5 + i * 0.5;
      group.add(plain);
    }
    let logoMesh = null;
    const textureLoader = new THREE.TextureLoader();
    textureLoader.load("/logos/bond_logo_white.png", (texture) => {
      const aspect = texture.image.width / texture.image.height;
      const logoWidth = 30;
      const logoHeight = logoWidth / aspect;
      const logoGeometry = new THREE.PlaneGeometry(logoWidth, logoHeight);
      const logoMaterial = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        opacity: 0,
      });
      logoMesh = new THREE.Mesh(logoGeometry, logoMaterial);
      logoMesh.position.x = 31.1;
      logoMesh.rotation.y = Math.PI / 2;
      logoMesh.visible = false;
      group.add(logoMesh);
    });
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);
    renderer.setClearColor("#000000");
    container.appendChild(renderer.domElement);
    let rotatevalue = 0.035;
    let acceleration = 0;
    let animatestep = 0;
    let toend = false;
    function easing(t, b, c, d) {
      t /= d / 2;
      if (t < 1) return (c / 2) * t * t + b;
      t -= 2;
      return (c / 2) * (t * t * t + 2) + b;
    }
    function renderScene() {
      animatestep = Math.max(0, Math.min(240, toend ? animatestep + 1 : animatestep - 4));
      acceleration = easing(animatestep, 0, 1, 240);
      if (acceleration > 0.35) {
        const progress = (acceleration - 0.35) / 0.65;
        group.rotation.y = -Math.PI / 2 * progress;
        group.position.z = 50 * progress;
        const finalProgress = Math.max(0, (acceleration - 0.97) / 0.03);
        mesh.material.opacity = 1 - finalProgress;
        ringcover.material.opacity = finalProgress;
        ring.material.opacity = 0;
        if (logoMesh) {
          if (finalProgress > 0) {
            logoMesh.visible = true;
            logoMesh.material.opacity = finalProgress;
            const s = 0.5 + 0.5 * finalProgress;
            logoMesh.scale.set(s, s, 1);
          } else {
            logoMesh.visible = false;
          }
        }
      }
      renderer.render(scene, camera);
    }
    function animate() {
      mesh.rotation.x += rotatevalue + acceleration;
      renderScene();
      requestAnimationFrame(animate);
    }
    animate();
    function onWindowResize() {
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;
      camera.aspect = newWidth / newHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(newWidth, newHeight);
    }
    window.addEventListener("resize", onWindowResize);
    return () => {
      window.removeEventListener("resize", onWindowResize);
      if (renderer.domElement && container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  };

  return (
    <div className="loading-page">
      <div className="loading-content">
        <img src="/logos/bond_logo_white.png" alt="Bond Logo" className="bond-logo-loading" />
        <div className="three-container" ref={containerRef} />
        <p className="info">We are preparing your experience...</p>
      </div>
    </div>
  );
};

export default LoadingPage;